import { map, size } from 'lodash';
import type { FC } from 'react';
import React from 'react';

import AddressBlock from '../../../components/review/AddressBlock';
import '../../../components/review/Review.scss';
import ReviewFooter from '../../../components/review/ReviewFooter';
import ReviewItem from '../../../components/review/ReviewItem';
import ReviewSection from '../../../components/review/ReviewSection';
import useAppStoreRent from '../../../service/store/appRent';
import useOrderStoreRent from '../../../service/store/orderRent';
import { scrollToRef } from '../../../utils/scroll';
import { OPTION_DELIVERY, OPTION_PICKUP } from '../constants';

interface IStepReviewProps {
  onSubmit: () => Promise<void>;
}

const StepReview: FC<IStepReviewProps> = ({ onSubmit }) => {
  const setStep = useAppStoreRent(state => state.setStep);
  const order = useOrderStoreRent(state => state.order);

  const {
    products,
    delivery_type,
    delivery_option,
    delivery_date,
    pickup_option,
    pickup_date,
    customer_number,
    reference_number,
    coupon,
    address,
    diffDeliveryAddress,
    deliveryAddress,
    diffInvoiceAddress,
    invoiceAddress,
    diffInvoiceRecipient,
    invoiceRecipient
  } = order || {};

  const isDelivery = delivery_type === OPTION_DELIVERY;
  const isPickup = delivery_type === OPTION_PICKUP;

  const goToStep = (step: number) => (): void => {
    setStep(step);
    scrollToRef();
  };

  const handleSubmit = (): void => {
    void onSubmit();
    scrollToRef();
  };

  return (
    <div className="review">
      <ReviewSection
        icon="package"
        title="Mietgeräte"
        onEdit={goToStep(1)}
      >
        <ReviewItem
          label={`Ausgewählte Geräte (${size(products)})`}
          full
          value={size(products) > 0 ? (
            <div className="review-products">
              {map(products, product => (
                <div className="review-product" key={product.id}>
                  <span className="review-product-name">{product.name}</span>
                  <span className="review-product-qty">
                    {product.quantity} Stk.
                  </span>
                </div>
              ))}
            </div>
          ) : undefined}
        />
      </ReviewSection>

      <ReviewSection
        icon="truck"
        title="Versand & Abholung"
        onEdit={goToStep(2)}
      >
        <ReviewItem
          label="Art"
          value={isPickup ? 'Selbstabholung bei ToolService' : 'Versand'}
        />
        {isDelivery && (
          <>
            <ReviewItem label="Versandart" value={delivery_option?.name} />
            <ReviewItem label="Spätestens versenden am" value={delivery_date} />
          </>
        )}
        {isPickup && (
          <>
            <ReviewItem label="Abholort" value={pickup_option?.name} />
            <ReviewItem label="Abholdatum" value={pickup_date} />
          </>
        )}
      </ReviewSection>

      <ReviewSection
        icon="users"
        title="Kundendaten"
        onEdit={goToStep(2)}
      >
        <ReviewItem label="Kundennummer" value={customer_number} />
        <ReviewItem label="Referenznummer" value={reference_number} />
        <ReviewItem label="E-Mail" value={address?.email} />
        <ReviewItem label="Telefon" value={address?.phone} />
        <ReviewItem label="Mobilnummer" value={address?.mobile} />
        <ReviewItem label="Gutscheincode" value={coupon} />
        <ReviewItem
          label="Anschrift"
          full
          value={<AddressBlock address={address} />}
        />
      </ReviewSection>

      {isDelivery && diffDeliveryAddress && deliveryAddress && (
        <ReviewSection
          icon="pin"
          title="Abweichende Lieferadresse"
          onEdit={goToStep(2)}
        >
          <ReviewItem
            label="Lieferadresse"
            full
            value={<AddressBlock address={deliveryAddress} />}
          />
        </ReviewSection>
      )}

      {diffInvoiceAddress && invoiceAddress && (
        <ReviewSection
          icon="file"
          title="Abweichende Rechnungsadresse"
          onEdit={goToStep(2)}
        >
          <ReviewItem
            label="Rechnungsadresse"
            full
            value={<AddressBlock address={invoiceAddress} />}
          />
        </ReviewSection>
      )}

      {diffInvoiceRecipient && invoiceRecipient && (
        <ReviewSection
          icon="file"
          title="Abweichender Rechnungsempfänger"
          onEdit={goToStep(2)}
        >
          <ReviewItem
            label="E-Mail Rechnungsempfänger"
            value={invoiceRecipient.email}
          />
          <ReviewItem
            label="Rechnungsempfänger"
            full
            value={<AddressBlock address={invoiceRecipient} />}
          />
        </ReviewSection>
      )}

      <ReviewFooter
        onSubmit={handleSubmit}
        submitText="Mietauftrag verbindlich erteilen"
      />
    </div>
  );
};

export default StepReview;
